import { memo } from 'react';
import { useTheme } from 'styled-components';

import { useOtcCounterpartiesQueryData } from '@src/providers/Counterparties/useOtcCounterpartiesQueryData';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import type { DealRepoData } from '@src/types/OTC';
import { formatOtcDealDateTimeInTimezone } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  Divider,
  formatPercent,
  HStack,
  InlineFormattedNumber,
  isOTCRepoSecurity,
  Text,
  useSecuritiesContext,
  VStack,
} from '@talos/kyoko';
import { Wrapper } from '../OMSCard/styles';
import { DealCardHeader } from './DealCardHeader';
import { DealCardRow } from './DealCardRow';
import { resolveDealSecurityForCard } from './resolveDealSecurityForCard';
import { useOtcDealSettlementTimezone } from './useOtcDealSettlementTimezone';

interface DealCardRepoProps {
  deal: DealRepoData;
}

export const DealCardRepo = memo(function DealCardRepo({ deal }: DealCardRepoProps) {
  const theme = useTheme();
  const { subAccountsByName } = useSubAccounts();
  const { otcCounterpartiesByName } = useOtcCounterpartiesQueryData();
  const { securitiesBySymbol } = useSecuritiesContext();
  const settlementTimezone = useOtcDealSettlementTimezone(deal);
  const security = resolveDealSecurityForCard(securitiesBySymbol, deal);
  const productDetails = isOTCRepoSecurity(security) ? security.ProductDetails : undefined;
  const repoAsset = productDetails?.RepoAsset || deal.DealInfo.CollateralAsset || deal.DealInfo.EnteredQuantityAsset;
  const borrowedAsset = productDetails?.BorrowedAsset;
  const rate = formatPercent(deal.DealInfo.Rate);

  return (
    <Wrapper data-testid="repo-deal-card">
      <VStack gap="spacing8" alignItems="stretch" p="spacing4">
        <DealCardHeader deal={deal} />

        <Divider color={theme.colors.gray['000']} />

        <VStack gap="spacing4" alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="center" data-testid="repo-deal-card-summary">
            <Text size="fontSizeMd" weight="fontWeightMedium" data-testid="repo-deal-card-direction">
              {deal.DealInfo.Direction || 'N/A'}
            </Text>
            <VStack gap="spacing2" alignItems="flex-end" data-testid="repo-deal-card-amount">
              <InlineFormattedNumber number={deal.DealInfo.RepoAmount} currency={repoAsset} />
            </VStack>
          </HStack>

          <DealCardRow label="Rate" data-testid="repo-deal-card-rate">
            <InlineFormattedNumber number={rate ?? undefined} currency="%" showDashIfEmpty />
          </DealCardRow>

          <DealCardRow label="Purchase Price" data-testid="repo-deal-card-purchase-price">
            <InlineFormattedNumber number={deal.DealInfo.PurchasePrice} currency={borrowedAsset} />
          </DealCardRow>

          <DealCardRow label="Repurchase Price" data-testid="repo-deal-card-repurchase-price">
            <InlineFormattedNumber
              number={deal.DealInfo.RepurchasePrice}
              currency={borrowedAsset}
              showCurrency={!!deal.DealInfo.RepurchasePrice}
              showDashIfEmpty
            />
          </DealCardRow>

          <DealCardRow label="Prime Broker" data-testid="repo-deal-card-prime-broker">
            {deal.DealInfo.PrimeBroker || '—'}
          </DealCardRow>

          <DealCardRow label="Counterparty" data-testid="repo-deal-card-counterparty">
            {deal.Counterparty
              ? (otcCounterpartiesByName?.get(deal.Counterparty)?.DisplayName ?? deal.Counterparty)
              : '—'}
          </DealCardRow>

          <DealCardRow label="Repo Sub Account" data-testid="repo-deal-card-sub-account">
            {deal.SubAccount ? (subAccountsByName.get(deal.SubAccount)?.DisplayName ?? deal.SubAccount) : '—'}
          </DealCardRow>

          <DealCardRow label="Repurchase Date" data-testid="repo-deal-card-repurchase-date">
            {formatOtcDealDateTimeInTimezone(deal.DealInfo.ExpiryTime, settlementTimezone)}
          </DealCardRow>
        </VStack>
      </VStack>
    </Wrapper>
  );
});
